import { useAnimatedScroll } from "@faire/deprecated-retailer-visitor-shared/components/AnimatedBanner/useAnimatedScroll";
import { trackRetailerOnboardingTopPromoBannerView } from "@faire/web-api--source/events/retailerOnboarding/view/topPromoBanner";
import { trackRetailersRotatingTopBannerView } from "@faire/web-api--source/events/retailers/view/rotatingTopBanner";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import { Column, Flex } from "@faire/web/slate/Layout";
import { useIntersectionObserver } from "@faire/web/ui/hooks/useIntersectionObserver";
import _max from "lodash/max";
import React, { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";

interface IProps {
  banners: React.ReactNode[];
  className?: string;
  width?: string;
  /** Generic banner name list from PromoBannerType */
  bannerNames?: string[];
  /** list of faire value banner names in the order they appear in the banners prop for tracking */
  faireValuesBannerNames?: string[];
}

export const AnimatedBanner: React.FC<IProps> = ({
  banners,
  className,
  width,
  bannerNames,
  faireValuesBannerNames,
}) => {
  const bannersList: React.ReactNode[] = banners.filter(isNotNull);

  if (!bannersList.length) {
    return null;
  }

  return (
    <GridSingleRowContainer data-test-id="banners-grid" className={className}>
      {bannersList.map((banner, i) => (
        <HiddenBannerWrapper key={i}>{banner}</HiddenBannerWrapper>
      ))}
      <BannersContainer
        banners={banners}
        className={className}
        width={width}
        bannerNames={bannerNames}
        faireValuesBannerNames={faireValuesBannerNames}
      />
    </GridSingleRowContainer>
  );
};

const BannersContainer: React.FC<IProps> = ({
  banners,
  width,
  bannerNames,
  faireValuesBannerNames,
}) => {
  const { resumeScroll, pauseScroll, isVisible, currentIndex } =
    useAnimatedScroll(banners);

  const [isInView, setIsInView] = useState<boolean>(false);
  const bannerContainerRef = useIntersectionObserver((e) =>
    setIsInView(e.isIntersecting)
  );
  const bannerViewFlags = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (isInView && !bannerViewFlags.current.has(currentIndex)) {
      bannerViewFlags.current.add(currentIndex);
      if (faireValuesBannerNames) {
        // Legacy tracking for faire values banners
        trackRetailersRotatingTopBannerView(
          faireValuesBannerNames[currentIndex] ?? ""
        );
        trackRetailerOnboardingTopPromoBannerView(
          faireValuesBannerNames[currentIndex] ?? ""
        );
      } else if (bannerNames) {
        trackRetailerOnboardingTopPromoBannerView(
          bannerNames[currentIndex] ?? ""
        );
      }
    }
  }, [faireValuesBannerNames, currentIndex, isInView, bannerNames]);

  return (
    <BannerContainer
      onMouseEnter={pauseScroll}
      onMouseLeave={resumeScroll}
      data-test-id="banners-container"
      ref={bannerContainerRef}
    >
      <AnimatedWrapper
        $isVisible={isVisible}
        $width={width}
        data-test-id="animated-banners-wrapper"
        key={`animated-banner-${bannerNames?.[currentIndex]}`}
      >
        {banners[currentIndex]}
      </AnimatedWrapper>
    </BannerContainer>
  );
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(100%);
  }
  40% {
    opacity: 0;
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }`;

const AnimatedWrapper = styled(Flex).attrs({
  align: "center",
  justify: "center",
})<{ $isVisible: boolean; $width?: string }>`
  ${(props) =>
    props.$isVisible
      ? css`
          transform: translateY(100%);
          opacity: 0;
          animation: ${fadeIn} 500ms forwards;
        `
      : css`
          opacity: 1;
          animation: ${fadeOut} 500ms forwards;
        `}
  ${(props) =>
    props.$width
      ? css`
          width: ${props.$width};
        `
      : null}

  height: 100%;
`;

const GridSingleRowContainer = styled.div`
  display: grid;
  min-height: 36px;
`;

const HiddenBannerWrapper = styled.div`
  visibility: hidden;
  grid-column: 1;
  grid-row: 1;
  height: max-content;
  width: 100%;
`;

const BannerContainer = styled(Column).attrs({
  justify: "center",
})`
  grid-column: 1;
  grid-row: 1;
`;
